import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getAdminDuplicates, getAdminPeople, mergeAdminPeople, linkAdminAccount } from '../../api/client'
import PersonaPicker from '../../components/PersonaPicker'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import { detalleError } from '../../components/admin/detalle'

// Administración > Duplicados (people_registry.duplicados): the same person
// twice -- same email, or same name ignoring accents and case -- usually from
// the separate lists the register replaced, and accounts with no person that
// look like someone's.
//
// Merging is two steps: a dry run says what would move to the kept record
// and any conflict; only then the real one, which deletes the others and
// can't be undone.
//
// "Fusión manual" is for what no rule catches -- a first name only ("Gaston"),
// a nickname, a typo: pick the person to keep and the one to fold into them.
// The merged one's name stays as an alias of the kept one.

export default function AdminDuplicados() {
  const { t } = useTranslation(['admin', 'common'])
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState('')
  const [aviso, setAviso] = useState('')
  const [conservar, setConservar] = useState({})     // group key -> person id kept
  const [revision, setRevision] = useState(null)     // { grupo, target, resultados: [{ source, out }] }
  const [ocupado, setOcupado] = useState(false)
  const [todas, setTodas] = useState([])             // every person, inactive too, for the manual merge
  const [manual, setManual] = useState({ conservar: null, fusionar: null })

  const cargar = () => getAdminDuplicates()
    .then(d => { setDatos(d); setError('') })
    .catch(() => setError(t('duplicados.loadFailed')))
  const cargarTodas = () => getAdminPeople({ include_inactive: true, equipos: false }).then(setTodas).catch(() => {})
  useEffect(() => { cargar(); cargarTodas() }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  const claveGrupo = (g) => `${g.motivo}:${g.clave}`
  // Kept by default: the one linked to Buk, then one with a RUT, then the
  // fuller name, then an active one, then the oldest (lowest id, the list's
  // order) -- so "Gaston" folds into "Gaston Arnaldo Aros Galaz", not the reverse.
  const puntaje = (p) => [p.buk_id ? 1 : 0, p.rut ? 1 : 0, (p.name || '').trim().split(/\s+/).length, p.is_active ? 1 : 0]
  const mejor = (a, b) => {
    const x = puntaje(a), y = puntaje(b)
    for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] > y[i] ? a : b
    return a
  }
  const elegido = (g) => conservar[claveGrupo(g)] ?? g.personas.reduce(mejor).id

  const revisar = async (g, forzado) => {
    const target = forzado ?? elegido(g)
    const sources = g.personas.filter(p => p.id !== target)
    setOcupado(true)
    setError('')
    setAviso('')
    try {
      const resultados = []
      for (const s of sources) resultados.push({ source: s, out: await mergeAdminPeople(target, s.id, true) })
      setRevision({ grupo: g, target: g.personas.find(p => p.id === target), resultados })
    } catch (e) {
      setError(detalleError(e, t('duplicados.mergeFailed')))
    }
    setOcupado(false)
  }

  const fusionar = async () => {
    const { target, resultados } = revision
    setOcupado(true)
    setError('')
    let hechas = 0
    try {
      for (const { source } of resultados) {
        const out = await mergeAdminPeople(target.id, source.id, false)
        if (!out.ok) {
          setError(t('duplicados.mergeStopped', { name: source.name, conflictos: out.conflictos.join('; ') }))
          break
        }
        hechas += 1
      }
    } catch (e) {
      setError(detalleError(e, t('duplicados.mergeFailed')))
    }
    if (hechas) setAviso(t('duplicados.merged', { count: hechas, name: target.name }))
    if (hechas && revision.grupo.motivo === 'manual') setManual({ conservar: null, fusionar: null })
    setRevision(null)
    setOcupado(false)
    cargar(); cargarTodas()
  }

  const vincular = async (cuenta, persona) => {
    setError('')
    setAviso('')
    try {
      await linkAdminAccount(cuenta.id, persona.id)
      setAviso(t('duplicados.linked', { cuenta: cuenta.email || cuenta.display_name, name: persona.name }))
      cargar()
    } catch (e) {
      setError(detalleError(e, t('duplicados.linkFailed')))
    }
  }

  const conflictos = revision ? revision.resultados.flatMap(r => r.out.conflictos.map(c => `${r.source.name}: ${c}`)) : []

  return (
    <div className="p-8">
      <div className="mb-6">
        <PageHeader title={t('duplicados.title')} subtitle={t('duplicados.subtitle')} />
      </div>

      {error && <p className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}
      {aviso && <p className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">{aviso}</p>}

      {!datos ? (
        !error && <p className="text-gray-400 text-sm">{t('common:actions.loading')}</p>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('duplicados.manual.title')}</h2>
            <p className="text-xs text-gray-500 mb-3">{t('duplicados.manual.intro')}</p>
            <div className="bg-white border border-gray-200 rounded-xl px-5 py-4 grid gap-3 md:grid-cols-[1fr_1fr_auto] items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('duplicados.manual.conservar')}</label>
                <PersonaPicker value={manual.conservar} personas={todas.filter(p => p.id !== manual.fusionar)}
                  placeholder={t('duplicados.manual.buscar')}
                  onChange={id => setManual(m => ({ ...m, conservar: id ?? null }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">{t('duplicados.manual.fusionar')}</label>
                <PersonaPicker value={manual.fusionar} personas={todas.filter(p => p.id !== manual.conservar)}
                  placeholder={t('duplicados.manual.buscar')}
                  onChange={id => setManual(m => ({ ...m, fusionar: id ?? null }))} />
              </div>
              <button disabled={ocupado || !manual.conservar || !manual.fusionar}
                onClick={() => {
                  const a = todas.find(p => p.id === manual.conservar), b = todas.find(p => p.id === manual.fusionar)
                  revisar({ motivo: 'manual', clave: `${a.name} ← ${b.name}`, personas: [a, b] }, a.id)
                }}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
                {t('duplicados.fusionar')}
              </button>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('duplicados.personasTitle')}</h2>
            <p className="text-xs text-gray-500 mb-3">{t('duplicados.personasIntro')}</p>
            {!datos.personas.length ? (
              <p className="text-sm text-gray-400 bg-white border border-gray-200 rounded-xl px-5 py-6">{t('duplicados.personasEmpty')}</p>
            ) : (
              <div className="space-y-3">
                {datos.personas.map(g => {
                  const k = claveGrupo(g)
                  const target = elegido(g)
                  return (
                    <div key={k} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between gap-3 px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                        <span className="text-xs text-gray-500">
                          {t(`duplicados.motivo.${g.motivo}`)}: <span className="font-medium text-gray-700">{g.clave}</span>
                        </span>
                        <button onClick={() => revisar(g)} disabled={ocupado}
                          className="bg-gray-900 text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-gray-800 disabled:opacity-40">
                          {t('duplicados.fusionar')}
                        </button>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {g.personas.map(p => (
                            <tr key={p.id} className={`border-b border-gray-50 last:border-b-0 ${p.id === target ? 'bg-emerald-50/40' : ''}`}>
                              <td className="px-5 py-2 w-28">
                                <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                                  <input type="radio" name={k} checked={p.id === target} className="accent-emerald-600"
                                    onChange={() => setConservar(c => ({ ...c, [k]: p.id }))} />
                                  {p.id === target ? t('duplicados.conservar') : t('duplicados.fusionarEn')}
                                </label>
                              </td>
                              <td className={`px-3 py-2 font-medium ${p.is_active ? 'text-gray-900' : 'text-gray-400'}`}>
                                {p.name}
                                {!p.is_active && <span className="ml-2 text-[10px] font-normal text-gray-400">({t('personas.inactivo')})</span>}
                              </td>
                              <td className="px-3 py-2 text-gray-500">{p.email || <span className="text-gray-300">—</span>}</td>
                              <td className="px-3 py-2 text-gray-500 tabular-nums">{p.rut || <span className="text-gray-300">—</span>}</td>
                              <td className="px-5 py-2 text-right text-[11px] text-gray-300">#{p.id}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                })}
              </div>
            )}
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">{t('duplicados.cuentasTitle')}</h2>
            <p className="text-xs text-gray-500 mb-3">{t('duplicados.cuentasIntro')}</p>
            {!datos.cuentas.length ? (
              <p className="text-sm text-gray-400 bg-white border border-gray-200 rounded-xl px-5 py-6">{t('duplicados.cuentasEmpty')}</p>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('duplicados.columns.cuenta')}</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{t('duplicados.columns.sugeridas')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.cuentas.map(({ cuenta, personas }) => (
                      <tr key={cuenta.id} className="border-b border-gray-50 last:border-b-0 align-top">
                        <td className="px-5 py-3">
                          <p className="font-medium text-gray-900">{cuenta.display_name}</p>
                          <p className="text-xs text-gray-500">{cuenta.email || '—'}</p>
                        </td>
                        <td className="px-5 py-3">
                          <div className="space-y-1.5">
                            {personas.map(p => (
                              <div key={p.id} className="flex items-center justify-between gap-3">
                                <span className={p.is_active ? 'text-gray-700' : 'text-gray-400'}>
                                  {p.name}
                                  <span className="ml-2 text-xs text-gray-400">{[p.email, p.rut].filter(Boolean).join(' · ')}</span>
                                </span>
                                <button onClick={() => vincular(cuenta, p)}
                                  className="shrink-0 text-xs font-medium text-gray-500 hover:text-gray-900 border border-gray-200 rounded-lg px-2.5 py-1 hover:bg-gray-50">
                                  {t('duplicados.vincular')}
                                </button>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {revision && (
        <Modal title={t('duplicados.confirmTitle', { name: revision.target.name })} onClose={() => !ocupado && setRevision(null)} wide>
          <div className="space-y-4 text-sm">
            <p className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
              {t('duplicados.irreversible', { count: revision.resultados.length })}
            </p>

            {revision.resultados.map(({ source, out }) => {
              const movidas = Object.entries(out.movidas || {})
              return (
                <div key={source.id} className="border border-gray-200 rounded-lg p-3">
                  <p className="font-medium text-gray-900 mb-2">
                    {t('duplicados.desde', { source: source.name, id: source.id })}
                  </p>
                  {movidas.length ? (
                    <>
                      <p className="text-xs text-gray-500 mb-1">{t('duplicados.movidas')}</p>
                      <ul className="text-xs text-gray-700 space-y-0.5 mb-2">
                        {movidas.map(([k, n]) => (
                          <li key={k} className="flex justify-between gap-4">
                            <span className="font-mono text-gray-500">{k}</span>
                            <span className="tabular-nums">{n}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-xs text-gray-400 mb-2">{t('duplicados.nadaQueMover')}</p>
                  )}
                  {out.campos?.length > 0 && (
                    <p className="text-xs text-gray-600">
                      {t('duplicados.campos')}{' '}
                      <span className="font-medium">{out.campos.map(c => t(`duplicados.campo.${c}`, { defaultValue: c })).join(', ')}</span>
                    </p>
                  )}
                </div>
              )
            })}

            {conflictos.length > 0 && (
              <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <p className="font-medium mb-1">{t('duplicados.conflictos')}</p>
                <ul className="list-disc pl-5 text-xs space-y-0.5">
                  {conflictos.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setRevision(null)} disabled={ocupado}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">
                {t('common:actions.cancel')}
              </button>
              <button onClick={fusionar} disabled={ocupado || conflictos.length > 0}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-40">
                {ocupado ? t('duplicados.fusionando') : t('duplicados.confirmar')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
